import { useCallback, useEffect, useState } from "react";

import MODALS from "../constants/modals";
import ROLES from "../constants/roles";
import {
  IDailySheetReportRecordModalProps,
  IRoentgenReportRecordModalProps,
} from "../types/modal";
import { IRoentgenResponse } from "../types/classifiers";
import { IRoentgenReportRecordResponse } from "../types/roentgenReport";
import roentgenReportRecordSchema, {
  RoentgenReportRecordBase,
} from "../schemas/roentgenReportRecord";

import { useAppDispatch } from "../hooks/redux";
import { closeModalById, openModal } from "../features/modals";

import axios from "axios";
import moment from "moment";
import { useFormik } from "formik";

import {
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  Stack,
  DialogContent,
  TextField,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LoadingButton } from "@mui/lab";

import EmployeeAutocomplete from "../components/EmployeeAutocomplete";
import { Controller, useForm } from "react-hook-form";
import CurrencyInput from "../components/CurrencyInput";

const DailySheetReportRecordModal: React.FunctionComponent<
  IDailySheetReportRecordModalProps
> = ({ id, report, record, callback }) => {
  const dispatch = useAppDispatch();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [roentgens, setRoentgnes] = useState<IRoentgenResponse[]>([]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      _id: record?._id || undefined,
      patient: record?.patient || "",
      workplacePreparation: record?.workplacePreparation || 12,
      therapy: record?.therapy || 0,
      surgery: record?.surgery || 0,
      prosthetics: record?.prosthetics || 0,
      statePaid: record?.statePaid || 0,
      roentgen: record?.roentgen || 0,
    },

    // @ts-ignore
    // resolver: yupResolver(createDailySheetReportSchema),
  });

  const onSubmit = handleSubmit((values) => {
    setIsSubmitting(true);

    axios
      .put<{ _id: string }>(`/daily-sheets/${report}`, values)
      .then((response) => {
        setIsSubmitting(false);
        handleClose();
        callback();
        // callback(response.data._id);
      })
      .catch((err) => {
        console.log("err:", err);
        setIsSubmitting(false);
      });
  });

  const handleDelete = useCallback(() => {
    if (!record) {
      return;
    }

    setIsDeleting(true);

    dispatch(
      openModal({
        type: MODALS.Approve,
        title: "Vai tiešām vēlaties dzēst?",
        message: "Ieraksts tiks neatgriezeniski dzēsts",
        onApprove: () => {
          axios.delete(`/daily-sheets/${report}/${record._id}`).then((res) => {
            handleClose();
            callback();
          });
        },
        onReject: () => {
          setIsDeleting(false);
        },
      })
    );
  }, []);

  const handleClose = () => {
    dispatch(closeModalById(id));
  };

  return (
    <Dialog fullWidth={true} maxWidth="xs" open={true} onClose={handleClose}>
      <form onSubmit={onSubmit}>
        <DialogTitle>Pievienot ierakstu</DialogTitle>
        <DialogContent style={{ paddingTop: 20 }}>
          <Stack spacing={2}>
            <Controller
              name="patient"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Pacients"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="workplacePreparation"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  label="Darba vietas sagatavošana"
                  value={field.value}
                  onChange={field.onChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">€</InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <Controller
              name="therapy"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  label="Terapija"
                  value={field.value}
                  onChange={field.onChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">€</InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <Controller
              name="surgery"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  label="Ķirurģija"
                  value={field.value}
                  onChange={field.onChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">€</InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="prosthetics"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  label="Protezēšana"
                  value={field.value}
                  onChange={field.onChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">€</InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="statePaid"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  label="Bērni - valsts apmaksātie pakalpojumi"
                  value={field.value}
                  onChange={field.onChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">€</InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="roentgen"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  label="RTG"
                  value={field.value}
                  onChange={field.onChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">€</InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {record && (
            <LoadingButton
              loading={isDeleting}
              disabled={isSubmitting}
              variant="outlined"
              color="error"
              onClick={() => handleDelete()}
            >
              Dzēst
            </LoadingButton>
          )}

          <Stack direction="row" spacing={2} justifyContent="flex-end" flex={1}>
            <Button onClick={handleClose}>Atcelt</Button>
            <LoadingButton
              loading={isSubmitting}
              disabled={isDeleting}
              type="submit"
              variant="contained"
            >
              {record ? "Saglabāt" : "Pievienot"}
            </LoadingButton>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DailySheetReportRecordModal;
